import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

// Import cart-items component to ensure it's defined before cart drawer operations
// This prevents issues where morph creates new elements before the custom element is registered
import '@theme/cart-items';

/**
 * Detects if the user is in a Meta (Facebook/Instagram) in-app browser.
 * These browsers have viewport height calculation issues.
 * @returns {boolean}
 */
function isMetaInAppBrowser() {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  return /FBAN|FBAV|Instagram/i.test(ua);
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #isMetaBrowser = false;
  #resizeHandler = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // Detect Meta in-app browser once
    this.#isMetaBrowser = isMetaInAppBrowser();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#cleanupMetaBrowserFix();
  }

  /**
   * Fixes viewport height issues in Meta's in-app browser.
   * Sets the dialog height to window.innerHeight which is accurate.
   */
  #applyMetaBrowserFix() {
    if (!this.#isMetaBrowser) return;

    const dialog = this.refs?.dialog;
    if (!dialog) return;

    const setHeight = () => {
      // Only apply on mobile
      if (window.innerWidth <= 749) {
        dialog.style.height = `${window.innerHeight}px`;
        dialog.style.maxHeight = `${window.innerHeight}px`;
      } else {
        dialog.style.height = '';
        dialog.style.maxHeight = '';
      }
    };

    // Apply immediately
    setHeight();

    // Listen for resize/orientation changes
    this.#resizeHandler = setHeight;
    window.addEventListener('resize', this.#resizeHandler);
    window.addEventListener('orientationchange', this.#resizeHandler);
  }

  /**
   * Cleans up Meta browser fix event listeners.
   */
  #cleanupMetaBrowserFix() {
    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
      window.removeEventListener('orientationchange', this.#resizeHandler);
      this.#resizeHandler = null;
    }
  }

  /**
   * Handles the cart add event by morphing the section first, then opening the dialog.
   * This ensures the drawer displays updated content when transitioning from empty to filled state.
   * In Meta in-app browsers, redirects to cart page instead of opening drawer.
   * @param {CartAddEvent} event - The cart add event containing sections data.
   */
  #handleCartAdd = async (event) => {
    if (!this.hasAttribute('auto-open')) return;

    // Meta in-app browsers: redirect to cart page instead of drawer
    if (this.#isMetaBrowser) {
      window.location.href = '/cart';
      return;
    }

    // Get the section ID from the cart-items-component inside this drawer
    const cartItems = this.querySelector('cart-items-component');
    const sectionId = cartItems?.dataset.sectionId;
    const sectionHtml = event.detail?.data?.sections?.[sectionId];

    if (sectionHtml && sectionId) {
      // Morph the section first to update from empty state to filled state
      await morphSection(sectionId, sectionHtml);
      this.showDialog();
    } else {
      // Fallback: open immediately if no sections data available
      this.showDialog();
    }

    // Apply Meta in-app browser viewport fix after dialog is shown
    requestAnimationFrame(() => {
      this.#applyMetaBrowserFix();
    });
  };

  open() {
    // Meta in-app browsers: redirect to cart page instead of drawer
    if (this.#isMetaBrowser) {
      window.location.href = '/cart';
      return;
    }

    this.showDialog();

    // Apply Meta in-app browser viewport fix after dialog is shown
    requestAnimationFrame(() => {
      this.#applyMetaBrowserFix();
    });

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
    this.#cleanupMetaBrowserFix();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
